import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { plainText } from '@/components/form/richText';
import { Button } from '@/components/ui/Button';
import { initials } from '@/lib/names';

import { linkStatusLabel, type Arc } from './arcTypes';

/** How much of the introductory text the card shows. */
const EXCERPT = 160;

interface ArcHeaderCardProps {
  arc: Arc;
  readOnly: boolean;
  onEdit: () => void;
}

/**
 * What app users see at the top of the ARC: the logo (the ARC's own, or the
 * organization's), the name, the description, and whether the two links the
 * header buttons open have been set up - laid out as the wireframe draws it
 * (Part 2, page 4), with the links' state at the right.
 */
export function ArcHeaderCard({ arc, readOnly, onEdit }: ArcHeaderCardProps) {
  const intro = plainText(arc.header.intro_content);
  const logo = arc.header.logo;
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="arc-builder-header-card-heading"
      data-testid="arc-builder-header-card"
      sx={{ px: 3, py: 2.5 }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="arc-builder-header-card-heading"
            variant="subtitle1"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            ARC Header
          </Typography>
          <Typography variant="body2">
            Organization details shown at the top of your ARC
          </Typography>
        </Box>
        <Button
          variant="secondary"
          onClick={onEdit}
          disabled={readOnly}
          data-testid="arc-builder-edit-header-button"
        >
          Edit Header
        </Button>
      </Box>

      <Box
        data-testid="arc-builder-header-card-body"
        sx={{
          mt: 2,
          display: 'flex',
          columnGap: 3,
          rowGap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flex: '1 1 18rem',
            minWidth: 0,
          }}
        >
          {logo ? (
            <Avatar
              src={logo.url}
              alt={`${arc.title} logo`}
              data-testid="arc-builder-header-logo-image"
              sx={{ width: 56, height: 56, border: 1.5, borderColor: 'text.primary' }}
            />
          ) : (
            <Avatar
              aria-hidden
              data-testid="arc-builder-header-logo-empty"
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'background.paper',
                color: 'primary.main',
                border: 1.5,
                borderColor: 'text.primary',
                fontSize: 24,
              }}
            >
              {initials(arc.title)}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              data-testid="arc-builder-header-title"
              sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {arc.title}
            </Typography>
            <Typography
              variant="body2"
              color={arc.description ? 'text.primary' : 'text.secondary'}
              data-testid="arc-builder-header-description"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {arc.description || 'No description yet'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="arc-builder-header-intro"
              sx={{ mt: 0.5, overflowWrap: 'anywhere' }}
            >
              {intro
                ? intro.length > EXCERPT
                  ? `${intro.slice(0, EXCERPT).trimEnd()}…`
                  : intro
                : 'No introductory text yet'}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" data-testid="arc-builder-fundraising-link-value">
            Fundraising Link: {linkStatusLabel(arc.header.donate.link_status)}
          </Typography>
          <Typography variant="body2" data-testid="arc-builder-volunteer-link-value">
            Volunteer Link: {linkStatusLabel(arc.header.volunteer.link_status)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
